import React, { useState } from 'react';
import { X, Mail, Shield, CheckCircle, Copy, Check } from 'lucide-react';
import { getChain } from '../chainUtils';

/**
 * RequestBadgeModal - Modal explaining how to request an organization verification badge
 */
const RequestBadgeModal = ({ isOpen, onClose, address, chain }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  // Get chain object if chain is a string ID
  const chainObj = typeof chain === 'string' ? getChain(chain) : chain;
  const chainName = chainObj?.name || '[Blockchain Type]';

  const emailTemplate = `Dear CoinCommons Support Team,

I would like to request an organization badge for my address. Please find the required information below:

1. Blockchain Type: ${chainName}

2. Blockchain Address: ${address || '[Your Address]'}

3. Company Official Name: [Your Company Name]

4. Company Logo: [Attach logo file or provide URL]

5. Company Homepage URL: [Your Website URL]

6. Contact Email Address: [Your Official Email]

Thank you for your consideration.

Best regards,
[Your Name]`;

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(emailTemplate).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Request Organization Badge</h2>
              <p className="text-sm text-gray-600 mt-1">To enhance public trust</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* What is it */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              What is an Organization Badge?
            </h3>
            <p className="text-gray-700 leading-relaxed">
              An organization badge is a verified marker that confirms your address belongs to a legitimate organization or company.
              It displays your organization's name, logo, and other public information to enhance public trust and credibility.
            </p>
          </div>

          {/* Next Steps */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">The Process</h4>
            <p className="text-sm text-blue-800">
              If you are the crypto address's owner, copy below email template, fill related information, and send it to our support team. We will follow up with you to kick off the verification process.
              The data to be verified includes blockchain type, crypto address, email address, and organization information etc.
              It typically takes 7-14 business days.
            </p>
          </div>

          {/* Email Template */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Mail className="w-5 h-5 text-green-600" />
                Email Template
              </h3>
              <button
                onClick={handleCopyTemplate}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy Template
                  </>
                )}
              </button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
{emailTemplate}
              </pre>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Copy this template and fill in your information before sending the email.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
          <a
            href="mailto:support@coincommons.io?subject=Organization Badge Request"
            className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors font-medium inline-flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Send Email
          </a>
        </div>
      </div>
    </div>
  );
};

export default RequestBadgeModal;
